/**
 * 설계 가이드 규칙 편집 — 주제(세부주제) 묶음 → 규칙 「적용 상황 → 원칙」 + 예외/대안(2026-10-07 사용자 요구).
 *
 * 원칙·예외·대안은 **정의**로 쓰고, 예시는 각자의 **부연 설명**에 쓴다(2026-10-07 사용자 지적 — 자리표시자에 규칙 예시를
 * 적어 두던 것도 걷어냈다). 정의 칸에 예시 표지(「예:」 …)가 보이면 그 자리에서 옮기라고 알린다(막지는 않는다 — 서버
 * `model.exampleWarnings` 와 같은 잣대). 어떻게 쓰는지는 공용 「규칙 작성 가이드」 가 정한다.
 *
 * 근거·원천·변경이력은 **세부 정보**라 규칙 번호(또는 「세부」)를 눌렀을 때만 연다. 변경이력은 서버가 쓴다 — 여기서는 보여 주기만 하고,
 * 고칠 때 「변경 사유」 를 보탤 수 있을 뿐이다. 번호(R1, R2 …)는 묶음을 건너 이어지고 프롬프트·내려받은 문서의 번호와 같다.
 *
 * 화면 상태의 `key` 는 화면 안에서만 쓰는 이름표다(저장할 때 뺀다 — `toPayload`).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { FiPlus, FiX, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { storedTime } from '../../utils/storedTime';

/** 서버 상한(designGuide/model.js LIMITS)과 같아야 한다 — 다르면 화면에선 되는데 저장에서 거부된다 */
export const GUIDE_LIMITS = {
    principle: 300, when: 200, exWhen: 200, exAction: 200, exceptions: 5, explain: 500, exExplain: 300,
    rules: 40, sections: 20, topic: 60, basis: 1000, source: 300, note: 300,
};
/**
 * 예시 표지 — 서버 designGuide/model.js 의 EXAMPLE_RE 와 같아야 한다(다르면 화면과 저장 결과의 경고가 갈린다).
 * 한글은 유니코드 이스케이프로 적었다 — 화면 문구가 아니라 판정용 정규식이라 번역 대상이 아니다(i18n 가드가 잡지 않게).
 */
export const EXAMPLE_RE = /(^|[\s([\u300C,])(\uC608\s*[:)]|\uC608\uB97C\s*\uB4E4(\uC5B4|\uBA74)|\uC608\uCEE8\uB300|\uAC00\uB839|e\.g\.|for example|for instance|ex\))/i;
const looksExample = (v) => EXAMPLE_RE.test(String(v || ''));

let seq = 0;
const nextKey = () => { seq += 1; return `k${seq}`; };

export const blankRule = () => ({ key: nextKey(), when: '', principle: '', explain: '', exceptions: [], basis: '', source: '', history: [], changeNote: '' });
export const blankSection = () => ({ key: nextKey(), topic: '', subtopic: '', rules: [blankRule()] });

/** 서버(또는 변환 초안)의 묶음 → 화면 상태. 모르는 칸은 그대로 둔다(저장할 때 다시 실려 간다) */
export function toFormSections(sections) {
    const list = (Array.isArray(sections) ? sections : []).map(s => ({
        ...s,
        key: nextKey(), topic: s.topic || '', subtopic: s.subtopic || '',
        rules: (s.rules || []).map(r => ({
            ...r,
            key: nextKey(), when: r.when || '', principle: r.principle || '', explain: r.explain || '',
            exceptions: (r.exceptions || []).map(e => ({ ...e, when: e.when || '', action: e.action || '', explain: e.explain || '' })),
            basis: r.basis || '', source: r.source || '', history: r.history || [], changeNote: r.changeNote || '',
        })),
    }));
    return list.length ? list : [blankSection()];
}

/** 화면 상태 → 저장 본문. 이름표·이력은 빼고(이력은 서버가 쓴다), 빈 예외 줄은 버린다 */
export function toPayload(sections) {
    return sections.map(({ key, ...s }) => ({   // eslint-disable-line no-unused-vars
        ...s,
        rules: s.rules.map(({ key: _k, history, ...r }) => ({   // eslint-disable-line no-unused-vars
            ...r,
            exceptions: r.exceptions.filter(e => e.when.trim() || e.action.trim() || e.explain.trim()),
        })),
    }));
}

/** 저장 전에 화면이 먼저 막을 것 — 서버가 거부할 것을 미리 보인다 */
export function guideProblems(sections) {
    const rules = sections.flatMap(s => s.rules).filter(r => r.principle.trim() || r.when.trim() || r.explain.trim() || r.exceptions.length);
    return {
        ruleCount: rules.filter(r => r.principle.trim()).length,
        over: rules.some(r => r.principle.trim().length > GUIDE_LIMITS.principle || r.when.trim().length > GUIDE_LIMITS.when
            || r.explain.trim().length > GUIDE_LIMITS.explain
            || r.exceptions.some(e => e.when.trim().length > GUIDE_LIMITS.exWhen || e.action.trim().length > GUIDE_LIMITS.exAction
                || e.explain.trim().length > GUIDE_LIMITS.exExplain))
            || sections.some(s => s.topic.length > GUIDE_LIMITS.topic || s.subtopic.length > GUIDE_LIMITS.topic),
        // 상황·예외만 적고 원칙이 빈 규칙, 상황만 적고 대안이 빈 예외 — 서버가 거부한다
        incomplete: rules.some(r => !r.principle.trim())
            || rules.some(r => r.exceptions.some(e => (e.when.trim() || e.explain.trim()) && !e.action.trim())),
        // 정의 칸에 예시가 섞인 규칙 수 — 막지 않고 알린다
        exampleLike: rules.filter(r => looksExample(r.principle) || looksExample(r.when)
            || r.exceptions.some(e => looksExample(e.when) || looksExample(e.action))).length,
        tooMany: rules.length > GUIDE_LIMITS.rules || sections.length > GUIDE_LIMITS.sections,
    };
}

/** 이력 시각 — ISO 를 「YYYY-MM-DD HH:MM」 로(UTC) */
const atText = (at) => storedTime(at, 'min');

const TYPE_KEY = {
    created: 'assistant:designGuide.hist.created', modified: 'assistant:designGuide.hist.modified', copied: 'assistant:designGuide.hist.copied',
};

function RuleDetail({ rule, onChange, t }) {
    return (
        <div className="dg-ruleDetail">
            <label className="tcf-field">
                <span className="tcf-label">{t('assistant:designGuide.f.basis')}</span>
                <textarea className="tcf-input" rows={2} value={rule.basis} maxLength={GUIDE_LIMITS.basis}
                    placeholder={t('assistant:designGuide.basisPh')} onChange={(e) => onChange({ basis: e.target.value })} />
            </label>
            <label className="tcf-field">
                <span className="tcf-label">{t('assistant:designGuide.f.source')}</span>
                <input className="tcf-input" value={rule.source} maxLength={GUIDE_LIMITS.source}
                    placeholder={t('assistant:designGuide.sourcePh')} onChange={(e) => onChange({ source: e.target.value })} />
            </label>
            {rule.id ? (
                <label className="tcf-field">
                    <span className="tcf-label">{t('assistant:designGuide.f.changeNote')}</span>
                    <input className="tcf-input" value={rule.changeNote} maxLength={GUIDE_LIMITS.note}
                        placeholder={t('assistant:designGuide.changeNotePh')} onChange={(e) => onChange({ changeNote: e.target.value })} />
                </label>
            ) : null}
            <div className="tcf-label">{t('assistant:designGuide.f.history')}</div>
            {rule.history.length ? (
                <ul className="dg-hist">
                    {[...rule.history].reverse().map((h, i) => (
                        // eslint-disable-next-line react/no-array-index-key -- 이력은 덧붙기만 한다
                        <li key={i}>
                            <span className="ta-meta">{atText(h.at)}</span>{' '}
                            {t(TYPE_KEY[h.type] || 'assistant:designGuide.hist.modified')}
                            {h.note ? ` — ${h.note}` : ''}
                            {h.before && h.before.principle && h.before.principle !== rule.principle ? (
                                <div className="ta-meta">{t('assistant:designGuide.hist.before', { text: h.before.principle })}</div>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : <div className="tcf-hint">{t('assistant:designGuide.hist.none')}</div>}
        </div>
    );
}
RuleDetail.propTypes = { rule: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired, t: PropTypes.func.isRequired };

function GuideRulesEditor({ sections, onChange, openKey, onToggle, readOnly, t }) {
    const setSection = (si, patch) => onChange(sections.map((s, i) => (i === si ? { ...s, ...patch } : s)));
    const setRule = (si, ri, patch) => setSection(si, { rules: sections[si].rules.map((r, j) => (j === ri ? { ...r, ...patch } : r)) });
    const total = sections.reduce((n, s) => n + s.rules.length, 0);
    let no = 0;
    return (
        <fieldset className="dg-sections" disabled={readOnly}>
            {sections.map((s, si) => (
                <div className="dg-section" key={s.key}>
                    <div className="dg-secHead">
                        <input className="tcf-input" value={s.topic} placeholder={t('assistant:designGuide.topicPh')}
                            aria-label={t('assistant:designGuide.f.topic')} onChange={(e) => setSection(si, { topic: e.target.value })} />
                        <input className="tcf-input" value={s.subtopic} placeholder={t('assistant:designGuide.subtopicPh')}
                            aria-label={t('assistant:designGuide.f.subtopic')} onChange={(e) => setSection(si, { subtopic: e.target.value })} />
                        <button type="button" className="tcf-iconBtn" title={t('assistant:designGuide.removeSection')}
                            onClick={() => onChange(sections.length > 1 ? sections.filter((_, i) => i !== si) : [blankSection()])}>
                            <FiX size={11} />
                        </button>
                    </div>
                    {s.rules.map((r, ri) => {
                        no += 1;
                        const open = openKey === r.key;
                        const left = GUIDE_LIMITS.principle - r.principle.trim().length;
                        return (
                            <div className={`dg-rule${open ? ' dg-ruleOpen' : ''}`} key={r.key}>
                                <button type="button" className="dg-ruleNo" title={t('assistant:designGuide.detailTip')} onClick={() => onToggle(r.key)}>
                                    {open ? <FiChevronDown size={10} /> : <FiChevronRight size={10} />}R{no}
                                </button>
                                <div className="dg-ruleBody">
                                    <input className="tcf-input dg-when" value={r.when} placeholder={t('assistant:designGuide.whenPh')}
                                        onChange={(e) => setRule(si, ri, { when: e.target.value })} />
                                    <div className="dg-principle">
                                        <textarea className="tcf-input" rows={2} value={r.principle} placeholder={t('assistant:designGuide.rulePh')}
                                            onChange={(e) => setRule(si, ri, { principle: e.target.value })} />
                                        {/* 글자 수/상한 — 「남은 글자」 숫자만 두었더니 무엇인지 알 수 없었다(사용자 질문 2026-10-07) */}
                                        <span className={`tcf-hint dg-count${left < 0 ? ' gm-over' : ''}`}
                                            title={t('assistant:designGuide.charCountTip', { max: GUIDE_LIMITS.principle })}>
                                            {r.principle.trim().length}/{GUIDE_LIMITS.principle}
                                        </span>
                                    </div>
                                    {looksExample(r.principle) || looksExample(r.when) ? (
                                        <div className="dg-exampleWarn">{t('assistant:designGuide.exampleWarn')}</div>
                                    ) : null}
                                    {/* 부연 설명 — 원칙의 뜻 풀이와 예시는 여기에 */}
                                    <textarea className="tcf-input dg-explain" rows={1} value={r.explain} maxLength={GUIDE_LIMITS.explain + 50}
                                        placeholder={t('assistant:designGuide.explainPh')}
                                        onChange={(e) => setRule(si, ri, { explain: e.target.value })} />
                                    {r.exceptions.map((e, ei) => (
                                        // eslint-disable-next-line react/no-array-index-key -- 아직 저장 전인 줄은 고유한 키가 없다
                                        <div className="dg-excItem" key={ei}>
                                        <div className="dg-exc">
                                            <span className="dg-excTag">{t('assistant:designGuide.exception')}</span>
                                            <input className="tcf-input" value={e.when} placeholder={t('assistant:designGuide.excWhenPh')}
                                                onChange={(ev) => setRule(si, ri, { exceptions: r.exceptions.map((x, k) => (k === ei ? { ...x, when: ev.target.value } : x)) })} />
                                            <span className="dg-excArrow">→</span>
                                            <input className="tcf-input" value={e.action} placeholder={t('assistant:designGuide.excActionPh')}
                                                onChange={(ev) => setRule(si, ri, { exceptions: r.exceptions.map((x, k) => (k === ei ? { ...x, action: ev.target.value } : x)) })} />
                                            <button type="button" className="tcf-iconBtn" title={t('common:ui.delete')}
                                                onClick={() => setRule(si, ri, { exceptions: r.exceptions.filter((_, k) => k !== ei) })}>
                                                <FiX size={11} />
                                            </button>
                                        </div>
                                        {looksExample(e.when) || looksExample(e.action) ? (
                                            <div className="dg-exampleWarn dg-excSub">{t('assistant:designGuide.exampleWarnExc')}</div>
                                        ) : null}
                                        <input className="tcf-input dg-excSub dg-explain" value={e.explain} maxLength={GUIDE_LIMITS.exExplain + 50}
                                            placeholder={t('assistant:designGuide.excExplainPh')}
                                            onChange={(ev) => setRule(si, ri, { exceptions: r.exceptions.map((x, k) => (k === ei ? { ...x, explain: ev.target.value } : x)) })} />
                                        </div>
                                    ))}
                                    <div className="dg-ruleTools">
                                        <button type="button" className="tcf-linkBtn" disabled={r.exceptions.length >= GUIDE_LIMITS.exceptions}
                                            onClick={() => setRule(si, ri, { exceptions: [...r.exceptions, { when: '', action: '', explain: '' }] })}>
                                            <FiPlus size={10} /> {t('assistant:designGuide.addException')}
                                        </button>
                                        <button type="button" className="tcf-linkBtn" onClick={() => onToggle(r.key)}>
                                            {t(open ? 'assistant:designGuide.detailHide' : 'assistant:designGuide.detailShow')}
                                        </button>
                                        {!open && (r.basis || r.source || r.history.length) ? (
                                            <span className="ta-meta">
                                                {[r.source && t('assistant:designGuide.hasSource'), r.history.length && t('assistant:designGuide.histCount', { count: r.history.length })].filter(Boolean).join(' · ')}
                                            </span>
                                        ) : null}
                                    </div>
                                    {open ? <RuleDetail rule={r} t={t} onChange={(patch) => setRule(si, ri, patch)} /> : null}
                                </div>
                                <button type="button" className="tcf-iconBtn" title={t('common:ui.delete')}
                                    onClick={() => setSection(si, { rules: s.rules.length > 1 ? s.rules.filter((_, j) => j !== ri) : [blankRule()] })}>
                                    <FiX size={11} />
                                </button>
                            </div>
                        );
                    })}
                    <button type="button" className="tcf-btn" disabled={total >= GUIDE_LIMITS.rules}
                        onClick={() => setSection(si, { rules: [...s.rules, blankRule()] })}>
                        <FiPlus size={11} /> {t('assistant:designGuide.addRule')}
                    </button>
                </div>
            ))}
            <button type="button" className="tcf-btn" disabled={sections.length >= GUIDE_LIMITS.sections}
                onClick={() => onChange([...sections, blankSection()])}>
                <FiPlus size={11} /> {t('assistant:designGuide.addSection')}
            </button>
        </fieldset>
    );
}
GuideRulesEditor.propTypes = {
    sections: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    openKey: PropTypes.string,
    onToggle: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    t: PropTypes.func.isRequired,
};
GuideRulesEditor.defaultProps = { openKey: '', readOnly: false };

export default GuideRulesEditor;
